import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { DownloadRounded, DescriptionOutlined, KeyOutlined } from '@mui/icons-material';
import { CircularProgress } from '@mui/material';
import { getTeacherAssessment } from '../../services/api';
import { TeacherAssessmentDetail } from '../../types';
import { downloadAssessmentPdf } from '../../utils/assessmentPdf';

interface AssessmentDownloadMenuProps {
  token: string;
  assessmentId: number;
  /**
   * Already-loaded assessment to export as-is — the editor passes its in-memory
   * copy so the PDF matches what's on screen, unsaved edits included. Omit to
   * fetch the saved version on demand.
   */
  preview?: TeacherAssessmentDetail;
  /** 'icon' for list rows, 'button' for the editor header. */
  variant?: 'icon' | 'button';
  onError?: (message: string) => void;
}

/** Split idle/active so the open state never fights the base colour utilities. */
const TRIGGER_CLASSES: Record<'icon' | 'button', { base: string; idle: string; active: string }> = {
  icon: {
    base: 'rounded-lg p-1.5 transition-colors disabled:opacity-50',
    idle: 'text-slate-400 hover:bg-violet-50 hover:text-violet-600',
    active: 'bg-violet-100 text-violet-700',
  },
  button: {
    base: 'inline-flex items-center gap-1.5 rounded-xl border bg-white px-3 py-2 text-sm font-semibold transition-colors disabled:opacity-50',
    idle: 'border-[#E9E6F4] text-slate-700 hover:border-violet-400 hover:text-violet-700',
    active: 'border-violet-400 text-violet-700',
  },
};

const MENU_W = 240;
const MENU_H = 116; // two items — used only to decide whether to flip upwards

const AssessmentDownloadMenu: React.FC<AssessmentDownloadMenuProps> = ({
  token,
  assessmentId,
  preview,
  variant = 'icon',
  onError,
}) => {
  const [busy, setBusy] = useState(false);
  /**
   * The menu is rendered in a portal at fixed coordinates rather than absolutely
   * inside the row: the assessment list is an `overflow-y-auto` container, which
   * clips any child that sticks out past its edge — the lower rows' menus were
   * being cut off entirely.
   */
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [at, setAt] = useState<{ top: number; left: number } | null>(null);
  const open = at !== null;

  const place = useCallback(() => {
    const r = triggerRef.current?.getBoundingClientRect();
    if (!r) return;
    const below = r.bottom + 6;
    setAt({
      top: below + MENU_H > window.innerHeight ? Math.max(8, r.top - MENU_H - 6) : below,
      left: Math.max(8, Math.min(r.right - MENU_W, window.innerWidth - MENU_W - 8)),
    });
  }, []);

  // Fixed coordinates go stale as soon as anything moves the trigger, so follow
  // it on scroll/resize — closing instead would fire on the browser's own
  // focus-scroll when the button is clicked near the edge of the list.
  useEffect(() => {
    if (!open) return;
    const follow = () => {
      const r = triggerRef.current?.getBoundingClientRect();
      if (!r || r.bottom < 0 || r.top > window.innerHeight) setAt(null);
      else place();
    };
    window.addEventListener('scroll', follow, true);
    window.addEventListener('resize', follow);
    return () => {
      window.removeEventListener('scroll', follow, true);
      window.removeEventListener('resize', follow);
    };
  }, [open, place]);

  const download = async (includeAnswers: boolean) => {
    setAt(null);
    setBusy(true);
    try {
      const detail = preview ?? (await getTeacherAssessment(token, assessmentId));
      const sections = detail.sections || [];
      if (sections.length === 0) throw new Error('This assessment has no questions to download yet.');
      downloadAssessmentPdf(detail, sections, { includeAnswers });
    } catch (e: any) {
      onError?.(e?.message || 'Failed to build the PDF');
    } finally {
      setBusy(false);
    }
  };

  const items: { label: string; hint: string; icon: React.ReactNode; answers: boolean }[] = [
    {
      label: 'Question paper',
      hint: 'Questions only, ready to print',
      icon: <DescriptionOutlined sx={{ fontSize: 18 }} />,
      answers: false,
    },
    {
      label: 'Answer key',
      hint: 'Correct answers and rubrics',
      icon: <KeyOutlined sx={{ fontSize: 18 }} />,
      answers: true,
    },
  ];

  return (
    <div className="flex-none">
      <button
        ref={triggerRef}
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          if (open) setAt(null);
          else place();
        }}
        disabled={busy}
        title="Download PDF"
        aria-label="Download PDF"
        aria-haspopup="menu"
        aria-expanded={open}
        className={`${TRIGGER_CLASSES[variant].base} ${
          open ? TRIGGER_CLASSES[variant].active : TRIGGER_CLASSES[variant].idle
        }`}
      >
        {busy ? (
          <CircularProgress size={16} sx={{ color: '#7c3aed' }} />
        ) : (
          <DownloadRounded sx={{ fontSize: variant === 'button' ? 17 : 18 }} />
        )}
        {variant === 'button' && <span>Download</span>}
      </button>

      {at &&
        createPortal(
          <>
            {/* Click-anywhere-else backdrop. */}
            <div
              className="fixed inset-0 z-[100]"
              onClick={(e) => {
                e.stopPropagation();
                setAt(null);
              }}
            />
            <div
              role="menu"
              style={{ position: 'fixed', top: at.top, left: at.left, width: MENU_W }}
              className="z-[101] overflow-hidden rounded-xl border border-[#E9E6F4] bg-white shadow-lg shadow-slate-900/10"
              onClick={(e) => e.stopPropagation()}
            >
              {items.map((it) => (
                <button
                  key={it.label}
                  role="menuitem"
                  type="button"
                  onClick={() => download(it.answers)}
                  className="flex w-full items-start gap-2.5 px-3 py-2.5 text-left transition-colors hover:bg-violet-50"
                >
                  <span className="mt-0.5 flex-none text-violet-600">{it.icon}</span>
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold text-slate-800">{it.label}</span>
                    <span className="block text-xs text-slate-400">{it.hint}</span>
                  </span>
                </button>
              ))}
            </div>
          </>,
          document.body
        )}
    </div>
  );
};

export default AssessmentDownloadMenu;
